import React from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { Controller, useFormContext } from 'react-hook-form';

import FormLabel from './FormLabel';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { automationModules } from 'constants/automation';
import useActivityTypes from 'hooks/useActivityTypes';

export default function TrackTime({ index }) {
  const { control, watch, setValue } = useFormContext();

  const name = `actions[${index}].timeTracker.track_action_time`;
  const trackActionTimeWatched = watch(name);
  const timeTrackerDurationName = `actions[${index}].timeTracker.manual_total_hours`;
  const timeTrackerDescriptionName = `actions[${index}].timeTracker.description`;
  const timeTrackerActivityTypeIdName = `actions[${index}].timeTracker.activity_type_id`;

  const { activityTypes } = useActivityTypes({ status: 'active' }, false);

  const trigger = watch('trigger') || {};
  const isForLeadAutomation =
    watch('is_lead_automation') || (trigger?.module === automationModules.LEADS && trigger?.code !== 'TRIG_LEAD_CONV_CASE');

  if (isForLeadAutomation) {
    return null;
  }

  return (
    <>
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <FormLabel label="Track time for this action" />
          <Controller
            name={name}
            control={control}
            defaultValue={false}
            render={({ field: { name, value, onChange } }) => (
              <InputSwitch
                checked={value}
                value={value}
                name={name}
                onChange={(e) => {
                  onChange(e.value);
                  if (!e.value) {
                    setValue(timeTrackerDurationName, '');
                    setValue(timeTrackerActivityTypeIdName, '');
                  }
                }}
                className="input-switch"
              />
            )}
          />
        </div>
      </div>
      <div className="row g-3">
        {trackActionTimeWatched && (
          <div className="col-12 col-md-6">
            <FormLabel label="Time duration" />
            <div className="d-flex align-items-center">
              <Controller
                name={timeTrackerDurationName}
                control={control}
                defaultValue=""
                render={({ field: { name, value, onChange } }) => (
                  <InputNumber
                    className="input-shadow w-100"
                    placeholder="Enter time duration"
                    keyfilter="pnum"
                    value={value || ''}
                    name={name}
                    onChange={(e) => onChange(e?.value)}
                    inputId="withoutgrouping"
                    mode="decimal"
                    useGrouping={false}
                    locale="en-US"
                    minFractionDigits={1}
                    maxFractionDigits={3}
                  />
                )}
              />
              <span className="ms-2" id="cm-info-circle">
                <i className="fa fa-info-circle pointer" aria-hidden="true"></i>
              </span>
            </div>
            <Tooltip target="#cm-info-circle" position="top" className="case-tooltip">
              If you leave this field as empty or with zero value, the time duration will automatically be set to system generated duration.
            </Tooltip>
          </div>
        )}
      </div>

      {trackActionTimeWatched && (
        <div className="row g-3 mt-1">
          <div className="col-12">
            <FormLabel label="Automated time entry description" />
            <Controller
              name={timeTrackerDescriptionName}
              control={control}
              defaultValue=""
              render={({ field: { name, value, onChange } }) => (
                <TextSnippetWrapper
                  type="textarea"
                  value={value}
                  name={name}
                  onChange={(e) => onChange(e.target.value)}
                  className="input-shadow w-100"
                />
              )}
            />
          </div>
        </div>
      )}

      {trackActionTimeWatched && (
        <div className="row g-3 mt-1">
          <div className="col-12 col-md-6">
            <FormLabel label="Automatic time entry billing code" />
            <Controller
              name={timeTrackerActivityTypeIdName}
              control={control}
              defaultValue=""
              render={({ field: { name, value, onChange } }) => (
                <Dropdown
                  name={name}
                  value={value}
                  onChange={(e) => onChange(e.value)}
                  options={activityTypes || []}
                  optionLabel="activity_label"
                  optionValue="activity_type_id"
                  placeholder="Select billing code"
                  className="input-shadow w-100"
                  filter
                  filterBy="activity_type,activity_type_code"
                  showClear={value ? true : false}
                />
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}
